import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  ToastMessage,
  ToastState,
} from '../../interfaces/connected-components/toast';
import {ToastType} from '../../utils/enums';

/**
 * Represents the initial state for the toast slice.
 * @type {ToastState}
 */
const initialState: ToastState = {
  isVisible: false,
  title: '',
  message: '',
  type: ToastType.SUCCESS,
};

/**
 * Represents the toast slice.
 */
export const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    /**
     * Action reducer that shows the toast with the specified message.
     * @param {ToastState} state - The current state of the toast.
     * @param {PayloadAction<{ message: string }>} action - The action containing the message payload.
     * @returns {void}
     */
    showToast: (
      state: ToastState,
      action: PayloadAction<ToastMessage>,
    ): void => {
      state.title = action.payload.title;
      state.message = action.payload.message;
      state.type = action.payload.type || ToastType.SUCCESS;
      state.isVisible = true;
    },

    /**
     * Action reducer that hides the toast.
     * @param {ToastState} state - The current state of the toast.
     * @returns {void}
     */
    hideToast: (state): void => {
      state.isVisible = false;
    },
  },
});

/**
 * Action creator that triggers the showToast action.
 * @type {ActionCreatorWithPayload<{ message: string }, string>}
 */
export const {showToast, hideToast} = toastSlice.actions;

/**
 * The reducer function for the toast slice.
 * @type {Reducer<ToastState>}
 */
export default toastSlice.reducer;
